import { Link } from "react-router-dom";
import { Mail, Instagram, ArrowRight, Mic, Landmark, Zap, Languages } from "lucide-react";
import { DevistoLogo } from "@/components/DevistoLogo";
import { useLanguage } from "@/i18n";
import { useMarket, priceLabel } from "@/market";
import { COUNTRY_PAGES, COUNTRY_SLUGS } from "@/market/countryPages";

const productLinks = [
  { label: "Assistant de devis", to: "/#assistant" },
  { label: "Comment ça marche", to: "/#comment" },
  { label: "Facturation", to: "/#facturation" },
  { label: "Devisto vs logiciel de facturation", to: "/#comparatif" },
  { label: "__market__", to: "/#belge" },
  { label: "Fonctionnalités", to: "/#fonctionnalites" },
  { label: "Métiers couverts", to: "/#metiers" },
  { label: "Tarifs", to: "/tarifs" },
  { label: "À propos", to: "/a-propos" },
  { label: "Guides & blog", to: "/blog" },
  { label: "Facture Peppol 2026", to: "/facture-peppol-2026", be: true },
  { label: "Vidéo démo", to: "/video-demo" },
];

const accountLinks = [
  { label: "Créer mon compte artisan", to: "/studio/inscription" },
  { label: "Se connecter", to: "/connexion" },
  { label: "Contact", to: "/#contact" },
];

const reassurance = (copy) => [
  { icon: Mic, title: "Dictée sur chantier", text: "Vos constats deviennent des lignes de devis.", testId: "reassurance-voice" },
  { icon: Landmark, title: copy.reassurance.title, text: copy.reassurance.text, testId: "reassurance-belgian" },
  { icon: Zap, title: copy.einvoice.title, text: copy.einvoice.text, testId: "reassurance-peppol" },
  { icon: Languages, title: `Support ${copy.chips[copy.chips.length - 1].replace(" / ", " & ")}`, text: "Une vraie personne du métier, dans votre langue.", testId: "reassurance-support" },
];

const ColumnTitle = ({ children, className = "" }) => (
  <h3 className={`font-heading text-sm font-bold uppercase tracking-wider text-[#0F2645] ${className}`}>
    {children}
    <span className="mt-2 block w-8 h-0.5 bg-[#3A5CC8] rounded-full" />
  </h3>
);

export const Footer = () => {
  const { t, lang, setLang } = useLanguage();
  const { market, copy, countries } = useMarket();

  return (
    <footer data-testid="site-footer" className="bg-[#F6F8FD] border-t border-[#E2E7F7] pt-16 pb-8">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-[1.6fr_1fr_1fr_1.2fr] gap-12">
          <div>
            <a href="/" data-testid="footer-logo" className="inline-flex">
              <DevistoLogo className="h-9 w-auto" />
            </a>
            <p className="mt-5 text-sm leading-relaxed text-[#5C6A87] max-w-xs font-medium">
              {t("Le devis, l'intervention et la facture des artisans du bâtiment.")}
            </p>
            <p className="mt-4 text-sm leading-relaxed text-[#5C6A87] max-w-xs">
              {t("Capturez le chantier pendant la visite, envoyez le devis avant de repartir, facturez dès que c'est signé.")}
            </p>
            <a
              href="mailto:info@devis-to.be"
              data-testid="footer-contact-email"
              className="mt-5 inline-flex items-center gap-2.5 text-sm font-semibold text-[#0F2645] hover:text-[#3A5CC8] transition-colors duration-200"
            >
              <Mail className="w-4 h-4 text-[#3A5CC8]" strokeWidth={1.8} /> info@devis-to.be
            </a>
            <div className="mt-5">
              <a
                href="https://www.instagram.com/devis.to"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-social-instagram"
                aria-label="Instagram"
                className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-[#E2E7F7] bg-white text-[#5C6A87] hover:text-[#3A5CC8] hover:border-[#3A5CC8] transition-colors duration-200"
              >
                <Instagram className="w-4 h-4" />
              </a>
            </div>
          </div>

          <div>
            <ColumnTitle>{t("Le produit")}</ColumnTitle>
            <ul className="mt-5 space-y-3">
              {productLinks.filter((link) => !link.be || market.country === "BE").map((link) => (
                <li key={link.label}>
                  <a
                    href={link.to}
                    data-testid={`footer-product-${link.label === "__market__" ? "market" : link.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className="text-sm text-[#5C6A87] hover:text-[#3A5CC8] transition-colors duration-200"
                  >
                    {link.label === "__market__" ? t(copy.short) : t(link.label)}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <ColumnTitle>{t("Mon compte")}</ColumnTitle>
            <ul className="mt-5 space-y-3">
              {accountLinks.map((link) => (
                <li key={link.label}>
                  <a
                    href={link.to}
                    data-testid={`footer-account-${link.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className="text-sm text-[#5C6A87] hover:text-[#3A5CC8] transition-colors duration-200"
                  >
                    {t(link.label)}
                  </a>
                </li>
              ))}
            </ul>
            <ColumnTitle className="mt-8">{t("Devisto par pays")}</ColumnTitle>
            <ul className="mt-4 space-y-2" data-testid="footer-countries">
              {COUNTRY_SLUGS.map((s) => { const c = countries.find((x) => x.country === COUNTRY_PAGES[s].code); return (
                <li key={s}><Link to={COUNTRY_PAGES[s].path} data-testid={`footer-country-${COUNTRY_PAGES[s].code}`} className="text-sm text-[#5C6A87] transition-colors duration-200 hover:text-[#3A5CC8]"><span aria-hidden="true" className="mr-1.5">{c?.flag}</span>{t(c?.name || s)}</Link></li>
              ); })}
            </ul>
          </div>

          <div>
            <ColumnTitle>{t("Prêt à essayer ?")}</ColumnTitle>
            <p className="mt-5 text-sm leading-relaxed text-[#5C6A87]">
              {t("30 jours d'essai, puis")} <span data-testid="footer-price">{priceLabel(market)}</span>/{t("mois par société — vos ouvriers inclus.")}
            </p>
            <Link
              to="/studio/inscription"
              data-testid="footer-cta-signup"
              className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#3A5CC8] px-6 py-3 text-sm font-semibold text-white hover:bg-[#2F4CA8] transition-colors duration-200"
            >
              {t("Créer mon compte")} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 rounded-2xl border border-[#E2E7F7] bg-white p-6">
          {reassurance(copy).map((item) => (
            <div key={item.testId} data-testid={item.testId} className="flex items-start gap-3">
              <span className="mt-0.5 inline-flex items-center justify-center w-9 h-9 rounded-xl border border-[#3A5CC8]/20 bg-[#EEF1FC] text-[#3A5CC8] shrink-0">
                <item.icon className="w-4 h-4" />
              </span>
              <div>
                <p className="font-heading text-sm font-semibold uppercase tracking-wide text-[#0F2645]">{t(item.title)}</p>
                <p className="mt-1 text-sm text-[#5C6A87] leading-relaxed">{t(item.text)}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-10 pt-6 border-t border-[#E2E7F7] flex flex-col sm:flex-row items-center justify-between gap-4">
          <p data-testid="footer-legal" className="text-xs text-[#5C6A87] text-center sm:text-left">
            © {new Date().getFullYear()} Devisto · {t("TVA")} BE 0786.236.171 · {t("Belgique")}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <Link to="/mentions-legales" data-testid="footer-legal-link" className="text-xs text-[#5C6A87] hover:text-[#3A5CC8] transition-colors">
              {t("Mentions légales")}
            </Link>
            <Link to="/confidentialite" data-testid="footer-privacy-link" className="text-xs text-[#5C6A87] hover:text-[#3A5CC8] transition-colors">
              {t("Politique de confidentialité")}
            </Link>
            <Link to="/cgv" data-testid="footer-terms-link" className="text-xs text-[#5C6A87] hover:text-[#3A5CC8] transition-colors">
              {t("Conditions générales")}
            </Link>
            <button type="button" data-testid="footer-cookies-link" onClick={() => window.dispatchEvent(new Event("devisto:consent-open"))} className="text-xs text-[#5C6A87] hover:text-[#3A5CC8] transition-colors">
              {t("Cookies")}
            </button>
            <div className="inline-flex items-center gap-1 rounded-full border border-[#E2E7F7] bg-white p-1">
              {["fr", "nl", "es"].map((l) => (
                <button
                  key={l}
                  type="button"
                  data-testid={`footer-lang-${l}`}
                  onClick={() => setLang(l)}
                  aria-pressed={lang === l}
                  className={`rounded-full px-2.5 py-1 text-xs font-bold uppercase transition-colors duration-200 ${
                    lang === l ? "bg-[#3A5CC8] text-white" : "text-[#5C6A87] hover:text-[#0F2645]"
                  }`}
                >
                  {l}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
